import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, apiError } from "../lib/api";
import { ArrowLeft, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Download, Library } from "lucide-react";
import { toast } from "sonner";

const BULK_HEADERS = ["name", "company", "batch_number", "manufacturing_date", "expiry_date", "mrp", "purchase_price", "quantity", "location", "barcode", "low_stock_threshold", "composition"];
const BULK_SAMPLE = `Paracetamol 500mg,Sun Pharma,,,2026-01-15,25,15,100,A1,8901234567890,10,Paracetamol:500:mg
Amoxicillin 500mg,Cipla,AMX7788,2024-03-10,2026-03-10,85,55,50,B2,8901234567891,10,Amoxicillin:500:mg
Crocin Cold & Flu,GSK,CRC2401,,2026-02-12,60,40,80,A2,,15,Paracetamol:500:mg | Caffeine:30:mg`;

export default function StockBulkImport() {
  const nav = useNavigate();
  const [tab, setTab] = useState("file"); // 'file' | 'paste'
  const [file, setFile] = useState(null);
  const [paste, setPaste] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    // Must use axios (with Bearer token) — a plain window.open triggers a
    // 401 because the JWT cookie isn't sent on cross-origin sub-domain
    // downloads. We fetch as a blob, then trigger a download.
    try {
      const res = await API.get("/imports/medicines-template", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "medicines_bulk_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadMasterCatalog = async () => {
    try {
      const res = await API.get("/master-catalog/download", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `master_catalog_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Master catalog downloaded — fill expiry, price and qty and re-upload here");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const parseCsvLines = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    return lines.map((line, idx) => {
      const parts = line.split(",").map((s) => s.trim());
      if (parts.length < 8) throw new Error(`Row ${idx + 1}: expected ≥ 8 columns`);
      const [name, company, batch_number, manufacturing_date, expiry_date, mrp, purchase_price, quantity, location = "", barcode = "", lowThrRaw = "", compositionRaw = ""] = parts;
      if (!name || !company) throw new Error(`Row ${idx + 1}: missing name/company`);
      if (!expiry_date) throw new Error(`Row ${idx + 1}: missing expiry_date`);
      const mrpN = parseFloat(mrp); const ppN = parseFloat(purchase_price); const qN = parseInt(quantity);
      if (isNaN(mrpN) || isNaN(ppN) || isNaN(qN)) throw new Error(`Row ${idx + 1}: invalid MRP/Cost/Qty`);
      const lowThrN = lowThrRaw === "" ? 10 : parseInt(lowThrRaw);
      const composition = (compositionRaw || "").split("|").map((entry) => {
        const e = entry.trim();
        if (!e) return null;
        const segs = e.split(":").map((s) => s.trim());
        if (segs.length >= 3 && segs[1]) {
          const sN = parseFloat(segs[1]);
          return { salt: segs[0], strength: isNaN(sN) ? null : sN, unit: segs[2] || null };
        }
        return segs[0] ? { salt: segs[0], strength: null, unit: null } : null;
      }).filter(Boolean);
      return { name, company, batch_number, manufacturing_date, expiry_date, mrp: mrpN, purchase_price: ppN, quantity: qN, location, barcode, low_stock_threshold: isNaN(lowThrN) ? 10 : lowThrN, composition };
    });
  };

  const submitFile = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a file"); return; }
    setSaving(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/imports/medicines-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.inserted} medicines${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const submitPaste = async (e) => {
    e.preventDefault();
    setSaving(true); setResult(null);
    try {
      const items = parseCsvLines(paste);
      if (items.length === 0) throw new Error("No rows to import");
      const { data } = await API.post("/imports/medicines-bulk", { items });
      setResult({ inserted: data.inserted, skipped: 0, errors: [] });
      toast.success(`Imported ${data.inserted} medicines`);
    } catch (err) { toast.error(err?.message || apiError(err)); }
    finally { setSaving(false); }
  };

  // Redirect to stock list after successful import
  useEffect(() => {
    if (result && result.inserted > 0) {
      const t = setTimeout(() => nav("/stock"), 2500);
      return () => clearTimeout(t);
    }
  }, [result, nav]);

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-4xl page-enter" data-testid="bulk-import-page">
      <Link to="/stock" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#002FA7] mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Stock
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#10B981] to-[#047857] text-white flex items-center justify-center">
            <Upload className="w-6 h-6" />
          </div>
          <div>
            <div className="overline mb-1">Inventory</div>
            <h1 className="font-display text-3xl font-bold">Bulk Import Medicines</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={downloadMasterCatalog} className="btn-secondary" data-testid="download-master-catalog-btn">
            <Library className="w-4 h-4" /> Download Master Catalog
          </button>
          <button onClick={downloadTemplate} className="btn-ghost" data-testid="download-template-btn">
            <Download className="w-4 h-4" /> Blank Template
          </button>
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 text-sm text-emerald-900">
        <b>Tip · Faster setup:</b> Click <b>Download Master Catalog</b> above to get an Excel pre-filled with every medicine
        name your admin has curated — you only need to fill Expiry, MRP, Purchase Price and Quantity (highlighted yellow),
        then upload it back here.
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 text-sm">
        <div className="font-semibold text-blue-900 mb-2">How it works</div>
        <ol className="list-decimal pl-5 space-y-1 text-blue-800">
          <li>Download the sample Excel template (pre-filled with a header row, a label row, and 4 example rows).</li>
          <li>Fill in your rows — one medicine per row. Dates must be <code className="bg-white px-1 rounded">YYYY-MM-DD</code>.</li>
          <li>
            The <b>composition</b> column accepts the format
            <code className="bg-white px-1 rounded mx-1">Salt:Strength:Unit</code>
            with multiple salts separated by <code className="bg-white px-1 rounded">|</code>. Example:
            <code className="bg-white px-1 rounded ml-1">Paracetamol:500:mg | Caffeine:30:mg</code>.
          </li>
          <li>Upload the filled <b>.xlsx</b> (or <b>.csv</b>) — or switch to the "Paste CSV" tab below.</li>
          <li>Medicines will be added to your current branch.</li>
        </ol>
      </div>

      <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 mb-4 w-fit">
        <button onClick={() => setTab("file")} data-testid="tab-file"
          className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${tab === "file" ? "bg-[#002FA7] text-white" : "text-slate-600"}`}>
          Upload File
        </button>
        <button onClick={() => setTab("paste")} data-testid="tab-paste"
          className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${tab === "paste" ? "bg-[#002FA7] text-white" : "text-slate-600"}`}>
          Paste CSV
        </button>
      </div>

      {tab === "file" ? (
        <form onSubmit={submitFile} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-[#002FA7] transition-colors">
            <FileSpreadsheet className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              data-testid="bulk-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              id="bulk-file"
              className="hidden"
            />
            <label htmlFor="bulk-file" className="btn-primary cursor-pointer inline-flex">
              <Upload className="w-4 h-4" /> Choose File
            </label>
            <div className="text-xs text-slate-500 mt-3">
              {file ? <span className="font-mono text-slate-900">{file.name} · {(file.size / 1024).toFixed(1)} KB</span> : "Accepts .xlsx, .xls, or .csv"}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Link to="/stock" className="btn-ghost">Cancel</Link>
            <button type="submit" disabled={saving || !file} className="btn-primary" data-testid="upload-submit-btn">
              <Upload className="w-4 h-4" /> {saving ? "Uploading..." : "Upload & Import"}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={submitPaste} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
            <div className="font-semibold text-slate-700 mb-1">Column order:</div>
            <code className="font-mono text-[11px] text-slate-600">{BULK_HEADERS.join(", ")}</code>
          </div>
          <textarea
            required
            rows={14}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder={BULK_SAMPLE}
            data-testid="paste-textarea"
            className="w-full border border-slate-200 rounded-lg p-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
          />
          <button type="button" onClick={() => setPaste(BULK_SAMPLE)} className="text-xs text-[#002FA7] hover:underline">Insert sample rows</button>
          <div className="flex justify-end gap-2">
            <Link to="/stock" className="btn-ghost">Cancel</Link>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="paste-submit-btn">
              {saving ? "Importing..." : "Import Rows"}
            </button>
          </div>
        </form>
      )}

      {result && (
        <div className={`mt-5 rounded-xl p-5 border ${result.inserted ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="import-result">
          <div className="flex items-start gap-3">
            {result.inserted ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" /> : <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />}
            <div>
              <div className="font-display font-semibold">
                {result.inserted > 0 ? `Imported ${result.inserted} medicine${result.inserted === 1 ? "" : "s"}` : "Nothing imported"}
                {result.skipped ? <span className="text-sm font-normal ml-2">· {result.skipped} skipped</span> : ""}
              </div>
              {result.errors && result.errors.length > 0 && (
                <ul className="mt-2 text-xs text-slate-700 list-disc pl-5 space-y-0.5">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              {result.inserted > 0 && <div className="text-xs text-slate-500 mt-2">Redirecting to Stock…</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
